import './App.css';
import { useEffect, useRef, useState } from 'react';
import BootstrapMessaging from './bootstrapMessaging';
import AwacDemo from './awac/demo/AwacDemo';
import { t, useAwacLang } from './awac/i18n';
import { getAwacState, setAwacState } from './awac/awacSessionState';
import { track, AwacEvents } from './awac/analytics';

// Real plan data for the Lib Mont-Saint-Hilaire pilot — mirrors the data the
// Lia agent serves through the AWAC floor-plan / unit actions.
const PLANS = [
  { name: 'Plan A1', beds: 1, baths: 1, area: 665, price: 329900 },
  { name: 'Plan A2', beds: 1, baths: 1, area: 675, price: 334900 },
  { name: 'Plan B1', beds: 2, baths: 1, area: 885, price: 399900 },
  { name: 'Plan B2', beds: 2, baths: 2, area: 915, price: 414900 },
  { name: 'Plan C1', beds: 3, baths: 2, area: 1200, price: 499900 },
];

/**
 * Ask the chat bootstrapper (which listens for this event) to open Lia.
 * Optionally hands off a page-context message: it is stored as the session
 * pendingMessage BEFORE the event fires, and the conversation layer sends it
 * once the conversation is open (or immediately if it already is).
 * @param {{message?: string, source?: string}} [options]
 */
function openChat(options) {
  const message = options && options.message;
  const source = (options && options.source) || 'page';
  if (message) {
    setAwacState('pendingMessage', message);
  }
  window.dispatchEvent(new CustomEvent('awac:open-chat', { detail: { source } }));
}

/** Format a CAD price for the active locale (fr-CA: 329 900 $ / en-CA: $329,900). */
function formatPrice(value, lang) {
  try {
    return new Intl.NumberFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0,
    }).format(value);
  } catch (e) {
    return `$${value.toLocaleString('en-CA')}`;
  }
}

/** Hand-built layered mountain ridge — Mont Saint-Hilaire silhouette. */
function Ridge() {
  return (
    <div className="libRidge" aria-hidden="true">
      <svg viewBox="0 0 1440 190" preserveAspectRatio="none" role="presentation">
        <path
          d="M0,150 L180,108 L320,132 L520,70 L700,118 L900,52 L1080,110 L1260,84 L1440,128 L1440,190 L0,190 Z"
          fill="oklch(82% 0.035 165)"
        />
        <path
          d="M0,170 L220,134 L420,156 L640,104 L860,148 L1060,96 L1240,138 L1440,114 L1440,190 L0,190 Z"
          fill="oklch(58% 0.06 168)"
        />
        <path
          d="M0,184 L260,158 L500,176 L760,140 L1020,170 L1260,150 L1440,166 L1440,190 L0,190 Z"
          fill="oklch(34% 0.05 170)"
        />
      </svg>
    </div>
  );
}

/** FR / EN toggle — French first (Bill 96). */
function LanguageToggle() {
  const [lang, setLang] = useAwacLang();
  return (
    <div className="libLangToggle" role="group" aria-label="Langue / Language">
      <button
        type="button"
        className={'libLangBtn' + (lang === 'fr' ? ' libLangBtnActive' : '')}
        aria-pressed={lang === 'fr'}
        onClick={() => setLang('fr')}>
        FR
      </button>
      <span className="libLangSep" aria-hidden="true">·</span>
      <button
        type="button"
        className={'libLangBtn' + (lang === 'en' ? ' libLangBtnActive' : '')}
        aria-pressed={lang === 'en'}
        onClick={() => setLang('en')}>
        EN
      </button>
    </div>
  );
}

// Proactive nudge: appears once per session after 25s on the page OR once the
// visitor scrolls to the residences section — whichever comes first.
const NUDGE_DELAY_MS = 25000;

function ProactiveNudge() {
  useAwacLang();
  const [visible, setVisible] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    if (getAwacState('proactiveDismissed')) {
      return undefined;
    }

    function show() {
      if (firedRef.current) {
        return;
      }
      firedRef.current = true;
      setVisible(true);
      track(AwacEvents.PROACTIVE_SHOWN, {});
    }

    const timerId = setTimeout(show, NUDGE_DELAY_MS);

    function onScroll() {
      const section = document.getElementById('residences');
      if (section && section.getBoundingClientRect().top < window.innerHeight * 0.6) {
        show();
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    // If the visitor opens the chat on their own, the nudge is moot.
    function onChatOpened() {
      firedRef.current = true;
      setVisible(false);
      setAwacState('proactiveDismissed', true);
    }
    window.addEventListener('awac:open-chat', onChatOpened);

    return () => {
      clearTimeout(timerId);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('awac:open-chat', onChatOpened);
    };
  }, []);

  if (!visible) {
    return null;
  }

  function handleEngage() {
    setVisible(false);
    setAwacState('proactiveDismissed', true);
    track(AwacEvents.PROACTIVE_ENGAGED, {});
    openChat({ message: t('nudge.message'), source: 'nudge' });
  }

  function handleDismiss() {
    setVisible(false);
    setAwacState('proactiveDismissed', true);
  }

  return (
    <div className="libNudge" role="dialog" aria-label={t('nudge.text')}>
      <p className="libNudgeText">{t('nudge.text')}</p>
      <div className="libNudgeActions">
        <button type="button" className="libNudgeCta" onClick={handleEngage}>
          {t('nudge.cta')}
        </button>
        <button type="button" className="libNudgeDismiss" onClick={handleDismiss}>
          {t('nudge.dismiss')}
        </button>
      </div>
    </div>
  );
}

function LandingPage() {
  const [lang] = useAwacLang();

  // Keep the document language attribute in sync (Bill 96: FR default).
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <div className="lib">
      <nav className="libNav" aria-label="Main">
        <a className="libWordmark" href="/">
          Lib<span className="libWordmarkFull"> Mont-Saint-Hilaire</span>
        </a>
        <span className="libNavTag">{t('nav.pilot')}</span>
        <div className="libNavLinks">
          <a href="#residences">{t('nav.residences')}</a>
          <a href="#neighborhood">{t('nav.neighborhood')}</a>
          <LanguageToggle />
          <button className="libBtn libBtnPrimary" onClick={() => openChat({ source: 'nav' })}>
            {t('nav.chat')}
          </button>
        </div>
      </nav>

      <header className="libHero">
        {/* Looping aerial footage of Mont-Saint-Hilaire (muted, decorative).
            The ref forces the muted ATTRIBUTE — React only sets the property,
            which some browsers ignore when deciding autoplay eligibility. */}
        <video
          className="libHeroVideo"
          ref={(el) => {
            if (el) {
              el.muted = true;
              el.setAttribute('muted', '');
            }
          }}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="media/hero-poster.jpg"
          aria-hidden="true"
          tabIndex={-1}
        >
          <source src="media/hero-loop.mp4" type="video/mp4" />
        </video>
        <div className="libHeroScrim" aria-hidden="true" />
        <p className="libEyebrow">{t('hero.eyebrow')}</p>
        <h1>
          {t('hero.titlePre')}<em>{t('hero.titleEm')}</em>
        </h1>
        <p className="libHeroLede">{t('hero.lede')}</p>
        <div className="libHeroActions">
          <button className="libBtn libBtnPrimary" onClick={() => openChat({ source: 'hero' })}>
            {t('hero.chat')}
          </button>
          <a className="libBtn libBtnGhost" href="#residences">
            {t('hero.viewResidences')}
          </a>
        </div>
        <Ridge />
      </header>

      <main>
        <section className="libSection" id="residences">
          <div className="libSectionHead">
            <h2>{t('residences.title')}</h2>
            <p>{t('residences.lede')}</p>
          </div>
          <div className="libPlans">
            {PLANS.map((plan) => (
              <div className="libPlanRow" key={plan.name}>
                <div className="libPlanName">{plan.name}</div>
                <div className="libPlanCell">
                  <strong>
                    {plan.beds} {t('plan.bed')} · {plan.baths} {t('plan.bath')}
                  </strong>
                  {t('plan.layout')}
                </div>
                <div className="libPlanCell">
                  <strong>
                    {plan.area.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA')} {t('plan.sqft')}
                  </strong>
                  {t('plan.interior')}
                </div>
                <div className="libPlanCell">
                  <strong>{formatPrice(plan.price, lang)}</strong>
                  {t('plan.asking')}
                </div>
                <button
                  className="libPlanAsk"
                  onClick={() => openChat({ message: t('plan.askMessage', { plan: plan.name }), source: 'plan_row' })}>
                  {t('plan.ask')}
                </button>
              </div>
            ))}
          </div>
          <p className="libPlansNote">{t('plans.note')}</p>
        </section>

        <section className="libSection" id="neighborhood">
          <div className="libSectionHead">
            <h2>{t('neighborhood.title')}</h2>
          </div>
          <div className="libSplit">
            <div className="libProse">
              <p>{t('neighborhood.p1')}</p>
              <p>{t('neighborhood.p2')}</p>
            </div>
            <aside className="libAskList">
              <h3>{t('askList.title')}</h3>
              <ul>
                {['askList.1', 'askList.2', 'askList.3', 'askList.4'].map((key) => (
                  <li key={key}>
                    <button
                      type="button"
                      className="libAskItem"
                      onClick={() => openChat({ message: t(key), source: 'ask_list' })}>
                      {t(key)}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </section>
      </main>

      <section className="libCta">
        <div className="libCtaInner">
          <h2>{t('cta.title')}</h2>
          <p>{t('cta.lede')}</p>
          <div className="libChips">
            {['cta.chip1', 'cta.chip2', 'cta.chip3'].map((key) => (
              <button
                key={key}
                className="libChip"
                onClick={() => openChat({ message: t(key), source: 'cta_chip' })}>
                {t(key)}
              </button>
            ))}
          </div>
          <button className="libBtn libBtnLight" onClick={() => openChat({ source: 'cta' })}>
            {t('cta.start')}
          </button>
        </div>
      </section>

      <footer className="libFooter">
        <div className="libFooterInner">
          <div className="libFooterMark">Lib Mont-Saint-Hilaire</div>
          <p>{t('footer.blurb')}</p>
          <p className="libFooterFine">{t('footer.fine')}</p>
        </div>
      </footer>

      <ProactiveNudge />
      <BootstrapMessaging />
    </div>
  );
}

function App() {
  // AWAC demo harness: visit /?demo=awac to see the expand-and-render flow
  // without a live Salesforce connection. The production app is unaffected.
  // ?demo=awac forces the demo; ?app=1 forces the real client.
  // REACT_APP_DEFAULT_DEMO=1 (set for the Cloudflare/preview demo build) makes the
  // demo the default landing view without needing the query param.
  const params = new URLSearchParams(window.location.search);
  const defaultDemo = process.env.REACT_APP_DEFAULT_DEMO === '1';
  const wantsDemo = params.get('demo') === 'awac' || (defaultDemo && params.get('app') !== '1');
  if (wantsDemo) {
    return <AwacDemo />;
  }

  return <LandingPage />;
}

export default App;
